import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { isPostHogEnabled, posthog } from '@/posthog';

import { cioanalytics } from './cio';
import { type AnalyticsUserTraits } from './types';

/** US E.164 for Customer.io (+1XXXXXXXXXX). Empty when no number; does not prepend +1 to blanks. */
const formatCIOPhoneNumber = (phoneNumber: string): string => {
  const stripped = phoneNumber.trim();
  if (!stripped) {
    return '';
  }

  if (/^\+1\d{10}$/.test(stripped)) {
    return stripped;
  }

  if (/^\d{10}$/.test(stripped)) {
    return `+1${stripped}`;
  }

  if (/^1\d{10}$/.test(stripped)) {
    return `+${stripped}`;
  }

  return stripped;
};

/**
 * Utility function to identify a user in Customer.io and PostHog
 * No-op if Customer.io or PostHog is disabled
 */
export async function identifyUser(
  userContactEmail: string,
  traits?: Partial<AnalyticsUserTraits>
) {
  const state = useSessionStore.getState();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { primaryContactPhoneNumber: traitPhone, ...restTraits } = traits ?? {};
  const primaryContactPhoneNumber = formatCIOPhoneNumber(traitPhone ?? state.contactPhoneNumber);

  const newTraits: AnalyticsUserTraits = {
    recipient: state.patientRecipient,
    email: state.contactEmail, // DEPRECATED: CIO will explicitly use primaryContactEmail
    phone: state.contactPhoneNumber, // DEPRECATED: CIO will explicitly use primaryContactPhoneNumber
    phoneVerified: state.contactPhoneNumberVerified,
    patientFirstName: state.patientFirstName,
    patientLastName: state.patientLastName,
    patientDateOfBirth: state.patientDateOfBirth,
    patientGender: state.patientGender,
    patientTimezone: browserTimezone,
    primaryContactEmail: state.contactEmail,
    primaryContactPhoneNumber,
    primaryContactPhoneNumberVerified: state.contactPhoneNumberVerified,
    representativeFirstName: state.representativeFirstName,
    representativeLastName: state.representativeLastName,
    representativeRelationship: state.representativeRelationship,
    representativeAuthorizedForMedicalDecisions: state.representativeAuthorizedForMedicalDecisions,
    ...restTraits,
  };

  if (cioanalytics) {
    const user = await cioanalytics.user();

    cioanalytics.identify(userContactEmail, {
      ...newTraits,
      createdAt: user.traits()?.createdAt || new Date().toISOString(),
    });
  }

  if (isPostHogEnabled) {
    posthog.identify(userContactEmail, {
      ...newTraits,
    });
  }
}
